// Component: SwipeRow.jsx
// Purpose: Swipe-left-to-reveal-actions row, generalized from MobileWatchList's
//   inline SwipeRow. Wraps arbitrary `children` (usually a ListRow) in a
//   draggable face; dragging left uncovers a strip of tone-colored actions.
//   Drag physics are kept verbatim from the source: each action ~78px wide, snap
//   open past half-reveal or a fast flick (velocity.x < -400), else snap closed.
//   Styling in ds-components.css (.ds-swipe-*).
//
//   Action tone → color (from DS tokens / existing danger styling):
//     accent  → --color-accent fill, --fg-on-accent text
//     danger  → --danger fill, #fff text  (the swipe action is a SOLID red chip,
//               not a tint, so white reads better than any token fg — matches
//               the current watchlist Remove action verbatim)
//     neutral → --bg-3 fill, --fg-1 text
//
//   Single-open coordination: opening one row closes any other open row. A
//   lightweight module-level registry ({token, close}) tracks whichever row
//   is currently open — no per-caller API change, no listener while a row is
//   closed. Identity is a per-instance token (stable via useRef), not the
//   `close` function itself, since `close` is recreated every render. While a
//   row IS open (at most one at a time, by construction) it also listens for
//   outer scroll and closes itself — cheap because the listener only exists
//   during that row's open window, never while closed/idle.
//
// Usage:
//   <SwipeRow actions={[
//       { key: "trade", label: "Trade", icon: <Icon/>, tone: "accent", onClick },
//       { key: "remove", label: "Remove", icon: <Icon/>, tone: "danger", onClick },
//   ]}>
//     <ListRow … />
//   </SwipeRow>

import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { cx } from "./cx";
import { haptics } from "./haptics";

const ACTION_W = 78; // width of each revealed action (verbatim from source)

// Module-level: the currently-open row, if any.
let openRowToken = null;
let openRowClose = null;

export function SwipeRow({ children, actions = [], className, ...props }) {
  const controls = useAnimationControls();
  const [open, setOpen] = useState(false);
  const revealW = ACTION_W * actions.length;
  const token = useRef({}).current;

  const close = () => {
    controls.start({ x: 0 });
    setOpen(false);
    if (openRowToken === token) {
      openRowToken = null;
      openRowClose = null;
    }
  };
  const openActions = () => {
    //! Close whichever other row is currently open before opening this one.
    if (openRowToken && openRowToken !== token) {
      openRowClose?.();
    }
    openRowToken = token;
    openRowClose = close;
    controls.start({ x: -revealW });
    setOpen(true);
    haptics.tap(); // light confirm as the action strip reveals
  };

  // Unmounting while open must release the registry so a stale closer isn't
  // invoked (and isn't left blocking the NEXT row from opening).
  useEffect(() => {
    return () => {
      if (openRowToken === token) {
        openRowToken = null;
        openRowClose = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on outer scroll — only while open, so this never adds a listener
  // to rows sitting closed in a long list.
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => close();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={cx("ds-swipe", className)} {...props}>
      {/* Actions behind the row (revealed on swipe-left) */}
      <div className="ds-swipe-actions">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={cx("ds-swipe-action", `tone-${a.tone || "neutral"}`)}
            style={{ width: ACTION_W }}
            onClick={() => {
              close();
              a.onClick?.();
            }}
          >
            {a.icon && <span className="ds-swipe-action-icon">{a.icon}</span>}
            {a.label}
          </button>
        ))}
      </div>

      {/* Draggable face */}
      <motion.div
        className="ds-swipe-face"
        drag="x"
        dragConstraints={{ left: -revealW, right: 0 }}
        dragElastic={0.06}
        animate={controls}
        initial={{ x: 0 }}
        onDragEnd={(_, info) => {
          if (info.offset.x < -revealW / 2 || info.velocity.x < -400) openActions();
          else close();
        }}
        //! While open, a tap closes instead of falling through to the child.
        onClickCapture={(e) => {
          if (open) {
            e.stopPropagation();
            close();
          }
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default SwipeRow;
